"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { Ban, Eye, PencilLine, Trash2 } from "lucide-react";
import { Seal } from "@/features/home-v2/desk/Desk";
import { TILE_PLACEHOLDER } from "@/features/home-v2/tiles";
import { formatTokenSymbol } from "@/lib/asset-naming";
import { sanitizeUrl } from "@/lib/helpers";
import type { AuctionItem } from "../../types";
import { formatDateTime, formatPrice, STATUS_LABEL } from "../single/format";
import { ownedPermissions, type OwnedAction } from "./useOwnedAuctionActions";
const TONE: Record<string, "muted" | "live" | "gold" | "down"> = {
  draft: "muted",
  active: "live",
  settling: "gold",
  finished: "gold",
  canceled: "down"
};

/**
 * One auction in the owner's ledger — «برگ مزایده»: the work in a small gilt
 * frame; its state, name and piece; the stretch of time it runs, lit up to
 * now; the three figures the legacy card listed (start price, range, bids per
 * person) plus how many bids it has drawn; and the owner's actions, each by
 * AuctionListActions' rule.
 */
export default function MyAuctionEntry(t0) {
  const $ = _c(185);
  const {
    a,
    lang,
    nowMs,
    mounted,
    onAsk
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let canCancel;
  let canDelete;
  let canEdit;
  let href;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== a || $[3] !== isFa || $[4] !== lang || $[5] !== mounted || $[6] !== nowMs || $[7] !== t) {
    const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
    href = `/${lang}/auctions/${a.slug}`;
    let t12;
    if ($[22] !== a.image || $[23] !== a.model?.image || $[24] !== a.model?.logo || $[25] !== a.model_data?.image) {
      t12 = sanitizeUrl(a.image ?? undefined) || sanitizeUrl(a.model?.image ?? undefined) || sanitizeUrl(a.model?.logo ?? undefined) || sanitizeUrl(a.model_data?.image ?? undefined) || TILE_PLACEHOLDER;
      $[22] = a.image;
      $[23] = a.model?.image;
      $[24] = a.model?.logo;
      $[25] = a.model_data?.image;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    const image = t12;
    const work = a.model?.name || a.model_data?.name || `${a.model_type} #${a.model_id}`;
    const status = STATUS_LABEL[a.status];
    const t13 = a.currency?.symbol;
    let t14;
    if ($[27] !== t13) {
      t14 = formatTokenSymbol(t13);
      $[27] = t13;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const symbol = t14;
    let t15;
    if ($[29] !== a) {
      t15 = ownedPermissions(a);
      $[29] = a;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    ({
      canEdit,
      canCancel,
      canDelete
    } = t15);
    const bids = Array.isArray(a.bids) ? a.bids.length : null;
    let t16;
    if ($[31] !== a.start_time) {
      t16 = new Date(a.start_time).getTime();
      $[31] = a.start_time;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    const start = t16;
    let t17;
    if ($[33] !== a.expire_time) {
      t17 = new Date(a.expire_time).getTime();
      $[33] = a.expire_time;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    const end = t17;
    const span = end - start;
    let t18;
    if ($[35] !== mounted || $[36] !== nowMs || $[37] !== span || $[38] !== start) {
      t18 = mounted && Number.isFinite(span) && span > 0 ? Math.min(100, Math.max(0, (nowMs - start) / span * 100)) : 0;
      $[35] = mounted;
      $[36] = nowMs;
      $[37] = span;
      $[38] = start;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    const pct = t18;
    let t19;
    if ($[40] !== isFa || $[41] !== symbol) {
      t19 = v => <><bdi>{formatPrice(v, isFa)}</bdi>{symbol ? <small>{symbol}</small> : null}</>;
      $[40] = isFa;
      $[41] = symbol;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    const price = t19;
    t7 = "ma-entry";
    t8 = a.status;
    let t20;
    if ($[43] !== image) {
      t20 = <Image src={image} alt="" fill={true} sizes="(max-width: 767px) 76px, 104px" />;
      $[43] = image;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    let t21;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <span className="ma-corners" />;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    if ($[46] !== href || $[47] !== t20) {
      t9 = <Link href={href} className="ma-art" tabIndex={-1} aria-hidden={true}>{t20}{t21}</Link>;
      $[46] = href;
      $[47] = t20;
      $[48] = t9;
    } else {
      t9 = $[48];
    }
    const t22 = TONE[a.status] ?? "muted";
    const t23 = status ? isFa ? status[0] : status[1] : a.status;
    let t24;
    if ($[49] !== t22 || $[50] !== t23) {
      t24 = <Seal tone={t22}>{t23}</Seal>;
      $[49] = t22;
      $[50] = t23;
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    let t25;
    if ($[52] !== a.model_type || $[53] !== t) {
      t25 = a.model_type === "collection" ? t("\u06A9\u0627\u0644\u06A9\u0634\u0646", "Collection") : "NFT";
      $[52] = a.model_type;
      $[53] = t;
      $[54] = t25;
    } else {
      t25 = $[54];
    }
    let t26;
    if ($[55] !== t25) {
      t26 = <span className="ma-tag">{t25}</span>;
      $[55] = t25;
      $[56] = t26;
    } else {
      t26 = $[56];
    }
    const t27 = !a.is_published || undefined;
    let t28;
    if ($[57] !== a.is_published || $[58] !== t) {
      t28 = a.is_published ? t("\u0645\u0646\u062A\u0634\u0631\u0634\u062F\u0647", "Published") : t("\u0645\u0646\u062A\u0634\u0631\u0646\u0634\u062F\u0647", "Unpublished");
      $[57] = a.is_published;
      $[58] = t;
      $[59] = t28;
    } else {
      t28 = $[59];
    }
    let t29;
    if ($[60] !== t27 || $[61] !== t28) {
      t29 = <span className="ma-tag" data-off={t27}>{t28}</span>;
      $[60] = t27;
      $[61] = t28;
      $[62] = t29;
    } else {
      t29 = $[62];
    }
    let t30;
    if ($[63] !== t24 || $[64] !== t26 || $[65] !== t29) {
      t30 = <div className="ma-marks">{t24}{t26}{t29}</div>;
      $[63] = t24;
      $[64] = t26;
      $[65] = t29;
      $[66] = t30;
    } else {
      t30 = $[66];
    }
    let t31;
    if ($[67] !== a.title || $[68] !== href) {
      t31 = <h3><Link href={href} className="v2-focus">{a.title}</Link></h3>;
      $[67] = a.title;
      $[68] = href;
      $[69] = t31;
    } else {
      t31 = $[69];
    }
    let t32;
    if ($[70] !== work) {
      t32 = <p className="ma-work"><bdi>{work}</bdi></p>;
      $[70] = work;
      $[71] = t32;
    } else {
      t32 = $[71];
    }
    if ($[72] !== t30 || $[73] !== t31 || $[74] !== t32) {
      t10 = <div className="ma-body">{t30}{t31}{t32}</div>;
      $[72] = t30;
      $[73] = t31;
      $[74] = t32;
      $[75] = t10;
    } else {
      t10 = $[75];
    }
    const t33 = `${pct}%`;
    let t34;
    if ($[76] !== t33) {
      t34 = {
        "--p": t33
      };
      $[76] = t33;
      $[77] = t34;
    } else {
      t34 = $[77];
    }
    const t35 = t34 as React.CSSProperties;
    const t36 = pct > 0 || undefined;
    let t37;
    if ($[78] !== t) {
      t37 = t("\u0634\u0631\u0648\u0639", "Opens");
      $[78] = t;
      $[79] = t37;
    } else {
      t37 = $[79];
    }
    let t38;
    if ($[80] !== t37) {
      t38 = <small>{t37}</small>;
      $[80] = t37;
      $[81] = t38;
    } else {
      t38 = $[81];
    }
    let t39;
    if ($[82] !== a.start_time || $[83] !== isFa) {
      t39 = formatDateTime(a.start_time, isFa);
      $[82] = a.start_time;
      $[83] = isFa;
      $[84] = t39;
    } else {
      t39 = $[84];
    }
    let t40;
    if ($[85] !== t38 || $[86] !== t39) {
      t40 = <span>{t38}{t39}</span>;
      $[85] = t38;
      $[86] = t39;
      $[87] = t40;
    } else {
      t40 = $[87];
    }
    let t41;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <i className="ma-rail" aria-hidden={true}><b /></i>;
      $[88] = t41;
    } else {
      t41 = $[88];
    }
    let t42;
    if ($[89] !== t) {
      t42 = t("\u067E\u0627\u06CC\u0627\u0646", "Closes");
      $[89] = t;
      $[90] = t42;
    } else {
      t42 = $[90];
    }
    let t43;
    if ($[91] !== t42) {
      t43 = <small>{t42}</small>;
      $[91] = t42;
      $[92] = t43;
    } else {
      t43 = $[92];
    }
    let t44;
    if ($[93] !== a.expire_time || $[94] !== isFa) {
      t44 = formatDateTime(a.expire_time, isFa);
      $[93] = a.expire_time;
      $[94] = isFa;
      $[95] = t44;
    } else {
      t44 = $[95];
    }
    let t45;
    if ($[96] !== t43 || $[97] !== t44) {
      t45 = <span>{t43}{t44}</span>;
      $[96] = t43;
      $[97] = t44;
      $[98] = t45;
    } else {
      t45 = $[98];
    }
    if ($[99] !== t35 || $[100] !== t36 || $[101] !== t40 || $[102] !== t45) {
      t11 = <div className="ma-time" style={t35} data-started={t36}>{t40}{t41}{t45}</div>;
      $[99] = t35;
      $[100] = t36;
      $[101] = t40;
      $[102] = t45;
      $[103] = t11;
    } else {
      t11 = $[103];
    }
    t2 = "ma-figs";
    let t46;
    if ($[104] !== t) {
      t46 = t("\u0642\u06CC\u0645\u062A \u0634\u0631\u0648\u0639", "Start price");
      $[104] = t;
      $[105] = t46;
    } else {
      t46 = $[105];
    }
    let t47;
    if ($[106] !== t46) {
      t47 = <dt>{t46}</dt>;
      $[106] = t46;
      $[107] = t47;
    } else {
      t47 = $[107];
    }
    let t48;
    if ($[108] !== a.start_price || $[109] !== price) {
      t48 = <dd>{price(a.start_price)}</dd>;
      $[108] = a.start_price;
      $[109] = price;
      $[110] = t48;
    } else {
      t48 = $[110];
    }
    if ($[111] !== t47 || $[112] !== t48) {
      t3 = <div>{t47}{t48}</div>;
      $[111] = t47;
      $[112] = t48;
      $[113] = t3;
    } else {
      t3 = $[113];
    }
    let t49;
    if ($[114] !== t) {
      t49 = t("\u0628\u0627\u0632\u0647\u200C\u06CC \u0642\u06CC\u0645\u062A", "Price range");
      $[114] = t;
      $[115] = t49;
    } else {
      t49 = $[115];
    }
    let t50;
    if ($[116] !== t49) {
      t50 = <dt>{t49}</dt>;
      $[116] = t49;
      $[117] = t50;
    } else {
      t50 = $[117];
    }
    let t51;
    if ($[118] !== a.min_price || $[119] !== isFa) {
      t51 = formatPrice(a.min_price, isFa);
      $[118] = a.min_price;
      $[119] = isFa;
      $[120] = t51;
    } else {
      t51 = $[120];
    }
    let t52;
    if ($[121] !== t51) {
      t52 = <bdi>{t51}</bdi>;
      $[121] = t51;
      $[122] = t52;
    } else {
      t52 = $[122];
    }
    let t53;
    if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
      t53 = <i aria-hidden={true}>–</i>;
      $[123] = t53;
    } else {
      t53 = $[123];
    }
    let t54;
    if ($[124] !== a.max_price || $[125] !== isFa) {
      t54 = formatPrice(a.max_price, isFa);
      $[124] = a.max_price;
      $[125] = isFa;
      $[126] = t54;
    } else {
      t54 = $[126];
    }
    let t55;
    if ($[127] !== t54) {
      t55 = <bdi>{t54}</bdi>;
      $[127] = t54;
      $[128] = t55;
    } else {
      t55 = $[128];
    }
    let t56;
    if ($[129] !== symbol) {
      t56 = symbol ? <small>{symbol}</small> : null;
      $[129] = symbol;
      $[130] = t56;
    } else {
      t56 = $[130];
    }
    let t57;
    if ($[131] !== t52 || $[132] !== t55 || $[133] !== t56) {
      t57 = <dd>{t52}{t53}{t55}{t56}</dd>;
      $[131] = t52;
      $[132] = t55;
      $[133] = t56;
      $[134] = t57;
    } else {
      t57 = $[134];
    }
    if ($[135] !== t50 || $[136] !== t57) {
      t4 = <div>{t50}{t57}</div>;
      $[135] = t50;
      $[136] = t57;
      $[137] = t4;
    } else {
      t4 = $[137];
    }
    let t58;
    if ($[138] !== t) {
      t58 = t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0647\u0631 \u0646\u0641\u0631", "Bids per person");
      $[138] = t;
      $[139] = t58;
    } else {
      t58 = $[139];
    }
    let t59;
    if ($[140] !== t58) {
      t59 = <dt>{t58}</dt>;
      $[140] = t58;
      $[141] = t59;
    } else {
      t59 = $[141];
    }
    const t60 = <dd><bdi>{nf.format(a.max_bids_per_user)}</bdi></dd>;
    if ($[142] !== t59 || $[143] !== t60) {
      t5 = <div>{t59}{t60}</div>;
      $[142] = t59;
      $[143] = t60;
      $[144] = t5;
    } else {
      t5 = $[144];
    }
    t6 = bids !== null ? <div><dt>{t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "Bids")}</dt><dd><bdi>{nf.format(bids)}</bdi></dd></div> : null;
    $[2] = a;
    $[3] = isFa;
    $[4] = lang;
    $[5] = mounted;
    $[6] = nowMs;
    $[7] = t;
    $[8] = canCancel;
    $[9] = canDelete;
    $[10] = canEdit;
    $[11] = href;
    $[12] = t10;
    $[13] = t11;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    canCancel = $[8];
    canDelete = $[9];
    canEdit = $[10];
    href = $[11];
    t10 = $[12];
    t11 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[145] !== t2 || $[146] !== t3 || $[147] !== t4 || $[148] !== t5 || $[149] !== t6) {
    t12 = <dl className={t2}>{t3}{t4}{t5}{t6}</dl>;
    $[145] = t2;
    $[146] = t3;
    $[147] = t4;
    $[148] = t5;
    $[149] = t6;
    $[150] = t12;
  } else {
    t12 = $[150];
  }
  let t13;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Eye aria-hidden={true} />;
    $[151] = t13;
  } else {
    t13 = $[151];
  }
  let t14;
  if ($[152] !== t) {
    t14 = t("\u0645\u0634\u0627\u0647\u062F\u0647", "View");
    $[152] = t;
    $[153] = t14;
  } else {
    t14 = $[153];
  }
  let t15;
  if ($[154] !== href || $[155] !== t14) {
    t15 = <Link href={href} className="v2-btn v2-btn--ghost v2-btn--sm v2-focus">{t13}{t14}</Link>;
    $[154] = href;
    $[155] = t14;
    $[156] = t15;
  } else {
    t15 = $[156];
  }
  let t16;
  if ($[157] !== a.slug || $[158] !== canEdit || $[159] !== lang || $[160] !== t) {
    t16 = canEdit ? <Link href={`/${lang}/auctions/${a.slug}/edit`} className="v2-btn v2-btn--ghost v2-btn--sm v2-focus"><PencilLine aria-hidden={true} />{t("\u0648\u06CC\u0631\u0627\u06CC\u0634", "Edit")}</Link> : null;
    $[157] = a.slug;
    $[158] = canEdit;
    $[159] = lang;
    $[160] = t;
    $[161] = t16;
  } else {
    t16 = $[161];
  }
  let t17;
  if ($[162] !== a || $[163] !== canCancel || $[164] !== onAsk || $[165] !== t) {
    t17 = canCancel ? <button type="button" className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" onClick={() => onAsk(a, "cancel")}><Ban aria-hidden={true} />{t("\u0644\u063A\u0648", "Cancel")}</button> : null;
    $[162] = a;
    $[163] = canCancel;
    $[164] = onAsk;
    $[165] = t;
    $[166] = t17;
  } else {
    t17 = $[166];
  }
  let t18;
  if ($[167] !== a || $[168] !== canDelete || $[169] !== onAsk || $[170] !== t) {
    t18 = canDelete ? <button type="button" className="v2-btn v2-btn--ghost v2-btn--sm ma-danger v2-focus" onClick={() => onAsk(a, "delete")}><Trash2 aria-hidden={true} />{t("\u062D\u0630\u0641", "Delete")}</button> : null;
    $[167] = a;
    $[168] = canDelete;
    $[169] = onAsk;
    $[170] = t;
    $[171] = t18;
  } else {
    t18 = $[171];
  }
  let t19;
  if ($[172] !== t15 || $[173] !== t16 || $[174] !== t17 || $[175] !== t18) {
    t19 = <div className="ma-acts">{t15}{t16}{t17}{t18}</div>;
    $[172] = t15;
    $[173] = t16;
    $[174] = t17;
    $[175] = t18;
    $[176] = t19;
  } else {
    t19 = $[176];
  }
  let t20;
  if ($[177] !== t10 || $[178] !== t11 || $[179] !== t12 || $[180] !== t19 || $[181] !== t7 || $[182] !== t8 || $[183] !== t9) {
    t20 = <article className={t7} data-status={t8}>{t9}{t10}{t11}{t12}{t19}</article>;
    $[177] = t10;
    $[178] = t11;
    $[179] = t12;
    $[180] = t19;
    $[181] = t7;
    $[182] = t8;
    $[183] = t9;
    $[184] = t20;
  } else {
    t20 = $[184];
  }
  return t20;
}